/**
 * Helpers for Create Shipment from Allocations — client validation and
 * API error formatting so users get actionable messages instead of a
 * false success toast.
 */

const SHIPPABLE_STATUSES = ["Cargo Ready", "Fully Allocated", "Partially Allocated"];

/**
 * Whether a purchase order is the dropship type.
 *
 * The list and allocation payloads send type as a choice pair or a plain id.
 *
 * @param {object|undefined} order
 * @return {boolean}
 */
const isDropshipPurchaseOrder = order => {
  const type = order?.type;
  if (!type) {
    return false;
  }
  if (typeof type === "object") {
    return type.id === "dropship";
  }
  return type === "dropship";
};

/**
 * Collect human-readable strings from nested DRF / RTK error payloads.
 *
 * @param {*} value
 * @return {!Array<string>}
 */
const flattenErrorMessages = value => {
  if (value == null || value === "") {
    return [];
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return [String(value)];
  }
  if (Array.isArray(value)) {
    return value.flatMap(flattenErrorMessages);
  }
  if (typeof value === "object") {
    if (typeof value.msg === "string" && value.msg.trim()) {
      return [value.msg.trim()];
    }
    if (typeof value.detail === "string" && value.detail.trim()) {
      return [value.detail.trim()];
    }
    return Object.values(value).flatMap(flattenErrorMessages);
  }
  return [];
};

/**
 * Flatten an RTK Query / DRF error payload into a toast string.
 *
 * Nested list errors (many=True serializers) must not render as
 * ``[object Object]``.
 *
 * @param {object|string|undefined} error
 * @param {string=} fallback
 * @return {string}
 */
export const getAllocationApiErrorMessage = (
  error,
  fallback = "Something went wrong while creating the shipment. Please try again.",
) => {
  const data = error?.data ?? error;
  if (!data) {
    return fallback;
  }
  if (typeof data === "string") {
    return data;
  }
  const parts = flattenErrorMessages(data);
  return parts.length ? parts.join(" ") : fallback;
};

/**
 * Unique parent order labels for selected allocation rows.
 *
 * @param {Array<object>} rows
 * @param {"purchase"|"transfer"} allocation
 * @return {string[]}
 */
const getOrderLabels = (rows, allocation) => {
  const parentKey = allocation === "purchase" ? "purchase_order" : "transfer_order";
  const labels = [];
  const seen = new Set();
  rows.forEach(row => {
    const name = row?.[parentKey]?.name || `line ${row?.id}`;
    if (!seen.has(name)) {
      seen.add(name);
      labels.push(name);
    }
  });
  return labels;
};

/**
 * Resolve destination facility display value from an allocation row.
 *
 * @param {object} row
 * @param {"purchase"|"transfer"} allocation
 * @return {string|number|null|undefined}
 */
const getDestinationWarehouse = (row, allocation) => {
  if (allocation === "purchase") {
    return row?.purchase_order?.to_warehouse;
  }
  return row?.transfer_order?.to_warehouse?.name || row?.transfer_order?.to_warehouse;
};

/**
 * Client-side checks before calling create-shipment APIs.
 *
 * @param {Array<object>} selectedRows
 * @param {"purchase"|"transfer"} allocation
 * @return {string|null} Error message, or null when the selection looks valid.
 */
export const getCreateShipmentClientError = (selectedRows, allocation) => {
  if (!selectedRows?.length) {
    return "Select at least one allocation line to create a shipment.";
  }

  const notReady = selectedRows.filter(item => !SHIPPABLE_STATUSES.includes(item.status));
  if (notReady.length) {
    return (
      "Only lines with status Cargo Ready, Fully Allocated, or Partially Allocated " +
      "can be shipped. Remove lines that are not ready and try again."
    );
  }

  const dropshipOrders =
    allocation === "purchase"
      ? selectedRows.filter(item => isDropshipPurchaseOrder(item?.purchase_order))
      : [];
  if (dropshipOrders.length) {
    return "Cannot create a shipment for a dropship purchase order.";
  }

  const missingWarehouse = selectedRows.filter(item => !getDestinationWarehouse(item, allocation));
  if (missingWarehouse.length) {
    const labels = getOrderLabels(missingWarehouse, allocation);
    const joined = labels.join(", ");
    const orderKind = allocation === "purchase" ? "purchase order" : "transfer order";
    const verb = labels.length > 1 ? "have" : "has";
    return (
      `Cannot create shipment: ${joined} ${verb} no destination warehouse. ` +
      `Set a facility on the ${orderKind} and try again.`
    );
  }

  const zeroQty = selectedRows.filter(item => !(Number(item.allocated_qty) > 0));
  if (zeroQty.length) {
    const labels = zeroQty.map(item => {
      const parentKey = allocation === "purchase" ? "purchase_order" : "transfer_order";
      const orderName = item?.[parentKey]?.name;
      const product = item?.product?.number || item?.product_number;
      if (orderName && product) {
        return `${orderName} (${product})`;
      }
      return orderName || product || `line ${item.id}`;
    });
    const unique = [...new Set(labels)];
    const joined = unique.join(", ");
    const verb = unique.length > 1 ? "have" : "has";
    return (
      `Cannot create shipment: ${joined} ${verb} no allocated quantity. ` +
      "Allocate quantity first and try again."
    );
  }

  return null;
};

/**
 * Tooltip when Create Shipment is disabled or blocked client-side.
 *
 * @param {Array<object>} selectedRows
 * @param {"purchase"|"transfer"} allocation
 * @return {string|false}
 */
export const getCreateShipmentButtonTitle = (selectedRows, allocation) => {
  if (!selectedRows?.length) {
    return "Action required! Choose a row or order line.";
  }
  const clientError = getCreateShipmentClientError(selectedRows, allocation);
  return clientError || false;
};
